export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
}

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
}

export class HttpClient {
  protected readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  protected async request<T>(
    path: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { method = "GET", body, headers = {} } = options;

    const init: RequestInit = {
      method,
      credentials: "include",
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
    };

    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    try {
      const res = await fetch(`${this.baseUrl}${path}`, init);
      const text = await res.text();
      const data = text ? (JSON.parse(text) as T) : null;

      if (!res.ok) {
        const message =
          (data as Record<string, string> | null)?.message ??
          (data as Record<string, string> | null)?.error ??
          res.statusText;
        return { data: null, error: message, status: res.status };
      }

      return { data, error: null, status: res.status };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err.message : "Network error",
        status: 0,
      };
    }
  }

  protected get<T>(path: string, headers?: Record<string, string>) {
    return this.request<T>(path, { method: "GET", headers });
  }

  protected post<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: "POST", body });
  }

  protected put<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: "PUT", body });
  }

  protected patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: "PATCH", body });
  }

  protected delete<T>(path: string) {
    return this.request<T>(path, { method: "DELETE" });
  }
}
